import { FC, ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { Company, SharePlan, useFormat, useModalState } from '../../../hooks/index';
import { AppNotification } from '../../../types/notification.types';
import { PageRoute } from '../../../types/page.types';
import { sharePlanTypeTitle } from '../../../types/pool-plans.types';
import { TargetBasedVestingMilestoneItem } from '../../../types/vesting-tasks.types';
import { getTranslation } from '../../../utils/get-translation.util';
import { AppFormattedMessage } from '../../app-formatted-message/index';
import Button from '../../button/index';
import { useIntervalFromToday } from '../format-date.util';

type NotificationProps = {
  createdAt: Date;
  handleCloseModal: () => void;
  isRead: boolean;
  milestones: TargetBasedVestingMilestoneItem[];
  onMarkRead: () => void;
  resolved: boolean;
  setMilestoneIndex: (index: null | number) => void;
  type: ReactNode | string;
} & Pick<AppNotification, 'targetBasedMilestoneRequest'>;

export const ApproveNotification: FC<NotificationProps> = ({
  createdAt,
  isRead,
  onMarkRead,
  resolved,
  type,
  setMilestoneIndex,
  milestones,
  targetBasedMilestoneRequest,
  handleCloseModal,
}) => {
  const { selectedCompanyId: companyId } = Company.useSelected();
  const date = useIntervalFromToday(createdAt);
  const { sharePlan } = SharePlan.useSharePlan({
    companyId: companyId,
    planId: targetBasedMilestoneRequest?.sharePlanId || '',
  });

  const { setParamValue } = useModalState('target-based-approve-modal');
  const [read, setRead] = useState(false);
  const { format } = useFormat();
  const navigate = useNavigate();

  const milestoneIndex = targetBasedMilestoneRequest
    ? milestones?.findIndex(
        (milestone) =>
          milestone.name === targetBasedMilestoneRequest.name &&
          milestone.weight === targetBasedMilestoneRequest.weight &&
          milestone.targetDate === targetBasedMilestoneRequest.targetDate &&
          milestone.sharePlanId === targetBasedMilestoneRequest.sharePlanId,
      )
    : null;

  const resolvedMilestoneIndex =
    typeof milestoneIndex === 'number' && milestoneIndex >= 0 ? milestoneIndex : null;

  const isCompleted =
    resolvedMilestoneIndex !== null
      ? milestones?.[resolvedMilestoneIndex]?.isCompleted
      : targetBasedMilestoneRequest?.isCompleted;

  if (resolved) return null;

  const onEditMilestoneDateClick = () => {
    const currentPath = window.location.pathname;
    const newPath = `${PageRoute.POOL_PLANS}?openEditPlanModal=true&paramPlanId=${sharePlan?.id}`;

    if (currentPath === PageRoute.POOL_PLANS) {
      handleCloseModal();
      navigate(newPath, { replace: true });
    } else {
      handleCloseModal();
      navigate(newPath);
    }
  };

  return (
    <div
      className={twMerge(
        'flex flex-col gap-4 rounded-lg border border-brand-50 bg-brand-25 px-4 py-3',
        (isRead || read) && 'border-gray-200 bg-white',
      )}
    >
      <div className="flex w-full justify-between text-label-md font-medium text-gray-500">
        <span>{type}</span>
        <span>{date}</span>
      </div>
      <div className="flex w-full gap-3">
        <div className="size-10 shrink-0 rounded-full bg-brand-700" />
        <div className="flex w-full flex-col gap-4">
          <span className="text-sm font-[450] text-black">
            {`${getTranslation('MILESTONE')} ${targetBasedMilestoneRequest?.name} ${getTranslation('UNDER_TARGET_BASED')} ${sharePlan?.name} (${sharePlan?.type && sharePlanTypeTitle[sharePlan.type]}) ${getTranslation('HAS_SET_TARGET_DATE')} ${format(targetBasedMilestoneRequest?.targetDate, 'dd/MM/yyyy')}. ${getTranslation('WOULD_YOU_LIKE_APPROVE_TARGET')}`}
          </span>
          <div className="flex w-full gap-4 max-lg:flex-col lg:justify-between">
            {!(isRead || read) && (
              <>
                <Button
                  className="w-fit text-nowrap text-sm font-medium text-brand-700"
                  onClick={() => {
                    onMarkRead();
                    setRead(true);
                  }}
                  styleType="NONE"
                >
                  <AppFormattedMessage id="MARK_AS_READ" />
                </Button>
              </>
            )}
            {!isCompleted && (
              <div
                className="flex h-9 w-full justify-end gap-2 text-sm font-[450] text-gray-700"
                data-permission-required
              >
                <Button
                  className="w-fit rounded border border-gray-100 bg-white px-3 shadow-xs"
                  onClick={onEditMilestoneDateClick}
                  styleType="NONE"
                >
                  Edit Milestone Date
                </Button>
                <Button
                  className="w-fit px-4 font-[550] text-white"
                  onClick={() => {
                    setMilestoneIndex(resolvedMilestoneIndex);
                    setParamValue(targetBasedMilestoneRequest?.sharePlanId || '');
                  }}
                >
                  <AppFormattedMessage id="APPROVE" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
